import React from 'react';

/**
 * Subtle network-node + animated traffic-line overlay.
 * Sits absolute inside dark sections to add depth without noise.
 */
export default function NetworkNodes({ density = 'medium' }) {
  const NODES = density === 'high' ? 28 : density === 'low' ? 12 : 18;
  const seeded = React.useMemo(() => {
    // Stable pseudo-random positions (deterministic, no SSR mismatch)
    let s = 9301;
    const rng = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
    return Array.from({ length: NODES }, (_, i) => ({
      x: rng() * 100,
      y: rng() * 100,
      r: 1 + rng() * 1.4,
      d: i % 4 * 0.5 + rng() * 1.2,
    }));
  }, [NODES]);
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      preserveAspectRatio="none"
      viewBox="0 0 100 100"
      aria-hidden
    >
      <defs>
        <linearGradient id="nn-line" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(34,211,238,0)" />
          <stop offset="50%" stopColor="rgba(34,211,238,0.45)" />
          <stop offset="100%" stopColor="rgba(124,58,237,0)" />
        </linearGradient>
        <filter id="nn-glow">
          <feGaussianBlur stdDeviation="0.5" />
        </filter>
      </defs>
      {/* Diagonal traffic lines */}
      {[12, 30, 48, 66, 82].map((y, i) => (
        <line key={i} x1="0" y1={y} x2="100" y2={y - 4 + (i % 2) * 8} stroke="url(#nn-line)" strokeWidth="0.18" />
      ))}
      {/* Connect nodes that are close (constellation) */}
      {seeded.map((a, i) =>
        seeded.slice(i + 1).map((b, j) => {
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d > 18) return null;
          return (
            <line
              key={`${i}-${j}`}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke="rgba(34,211,238,0.18)"
              strokeWidth="0.1"
            />
          );
        })
      )}
      {/* Nodes */}
      {seeded.map((n, i) => (
        <circle
          key={i}
          cx={n.x}
          cy={n.y}
          r={n.r * 0.18}
          fill="#22D3EE"
          opacity="0.7"
          filter="url(#nn-glow)"
          className="pulse-glow"
          style={{ animationDelay: `${n.d}s` }}
        />
      ))}
    </svg>
  );
}
